import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Hero } from './components/Hero';
import { MarketingHero } from './components/MarketingHero';
import { ProductDemo } from './components/ProductDemo';
import { AICoworkers } from './components/AICoworkers';
import { StayInControl } from './components/StayInControl';
import { ToolComparison } from './components/ToolComparison';
import { HowItWorks } from './components/HowItWorks';
import { HowItWorksConnections } from './components/HowItWorksConnections';
import { AlwaysOn } from './components/AlwaysOn';
import { CapabilityGrid } from './components/CapabilityGrid';
import { QuickCapture } from './components/QuickCapture';
import { LivesInSlack } from './components/LivesInSlack';
import { ConnectedWorkflow } from './components/ConnectedWorkflow';
import { ComputerUse } from './components/ComputerUse';
import { Pricing } from './pages/Pricing';
import { WallOfLove } from './components/WallOfLove';
import { NarrativeSection } from './components/NarrativeSection';
import { MemorySection } from './components/MemorySection';
import { SecuritySection } from './components/SecuritySection';
import { PricingSection } from './components/PricingSection';
import { ClosingCTA } from './components/ClosingCTA';
import { Layout } from './components/Layout';
import { IntroSplash } from './components/IntroSplash';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { Contact } from './pages/Contact';
import { Careers } from './pages/Careers';
import { About } from './pages/About';
import { Enterprise } from './pages/Enterprise';
import { ClientManagement } from './pages/ClientManagement';
import { GrowthMarketing } from './pages/GrowthMarketing';
import { FoundersExecs } from './pages/FoundersExecs';
import { Signup } from './pages/Signup';
import { Blog } from './pages/Blog';
import { Research } from './pages/Research';
import { CaseStudies } from './pages/CaseStudies';
import { Changelog } from './pages/Changelog';
import { Security } from './pages/Security';
import { CompareChatGPT } from './pages/CompareChatGPT';
import { WatchGigaDemo } from './pages/WatchGigaDemo';
import { BookDemo } from './pages/BookDemo';
import { Dashboard } from './pages/Dashboard';
import { Bespoke } from './pages/Bespoke';
import { TaskletHome } from './pages/TaskletHome';
import { CopyModeProvider, CopyModeToggle } from './components/copy/copyMode';
function ScrollToTop() {
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    if (hash) {
      // Defer so the target route has mounted before we scroll to its anchor.
      const id = hash.replace('#', '');
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          return;
        }
        window.scrollTo(0, 0);
      });
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}
export function App() {
  const [layoutMode, setLayoutMode] = useState<'default' | 'tasklet'>('default');
  return <CopyModeProvider data-id="element-0">
      <ScrollToTop data-id="element-1" />
      <CopyModeToggle data-id="element-2" />
      <button onClick={() => setLayoutMode(m => m === 'default' ? 'tasklet' : 'default')} className="fixed bottom-4 left-36 z-[9999] bg-black text-white px-3 py-1.5 rounded-full text-xs font-mono font-bold shadow-lg hover:bg-neutral-800 transition-colors border border-white/20" data-id="element-3">
        LAYOUT: {layoutMode.toUpperCase()}
      </button>
      {/* Global Prado-style film grain over the entire app */}
      <div className="grain-global" aria-hidden="true" data-id="element-4" />
      <Routes data-id="element-5">
        <Route path="/signup" element={<Signup data-id="element-7" />} data-id="element-6" />
        <Route path="/login" element={<Signup data-id="element-9" />} data-id="element-8" />
        <Route path="/dashboard" element={<Dashboard data-id="element-11" />} data-id="element-10" />

        <Route path="/" element={<Layout data-id="element-13" />} data-id="element-12">
          <Route index element={<>
                {layoutMode === 'tasklet' ? <TaskletHome data-id="element-15" /> : <>
                    <IntroSplash data-id="element-16" />
                    <MarketingHero data-id="element-17" />
                  </>}
                <AICoworkers isTasklet={layoutMode === 'tasklet'} data-id="element-18" />
                {/* Unified dark "How it works + Connections" section. The two
                 original standalone sections are kept (commented) for rollback. */}
                <HowItWorksConnections data-id="element-19" />
                {/* "@mention Giga in any channel" — lands right after we've
                 shown how Giga works and what it connects to, as the natural
                 "and here's where you actually reach it" payoff. */}
                <LivesInSlack data-id="element-20" />
                {/* <HowItWorks /> */}
                {/* <ConnectedWorkflow /> */}
                {/* "Under the hood" — what actually powers Giga. Lands after we
                 show where you reach it (Slack) and before the capability grid,
                 as the "and here's the machine behind it" proof beat. */}
                <ComputerUse data-id="element-21" />
                {/* "Always On" temporarily hidden — file kept, restore anytime. */}
                {/* <AlwaysOn /> */}
                {/* CapabilityGrid removed per request — file kept, restore anytime. */}
                {/* <CapabilityGrid /> */}
                <QuickCapture data-id="element-22" />
                {/* Founder quote sits between the two product-demo moments as
                 a human narrative pause, and relieves the copy-heavy
                 Memory → ToolComparison stretch further down. */}
                <NarrativeSection data-id="element-23" />
                <ProductDemo data-id="element-24" />
                {/* StayInControl ("Built-in guardrails") temporarily hidden — can be restored later */}
                {/* <StayInControl /> */}
                {/* "Total Recall" (MemorySection) temporarily hidden to test a
                 cleaner flow — file kept, restore anytime. */}
                {/* <MemorySection /> */}
                <ToolComparison data-id="element-25" />
                <WallOfLove data-id="element-26" />
                <PricingSection data-id="element-27" />
                <ClosingCTA data-id="element-28" />
              </>} data-id="element-14" />
          <Route path="terms" element={<Terms data-id="element-30" />} data-id="element-29" />
          <Route path="privacy" element={<Privacy data-id="element-32" />} data-id="element-31" />
          <Route path="contact" element={<Contact data-id="element-34" />} data-id="element-33" />
          <Route path="careers" element={<Careers data-id="element-36" />} data-id="element-35" />
          <Route path="about" element={<About data-id="element-38" />} data-id="element-37" />
          <Route path="enterprise" element={<Enterprise data-id="element-40" />} data-id="element-39" />
          <Route path="bespoke" element={<Bespoke data-id="element-42" />} data-id="element-41" />
          <Route path="pricing" element={<Pricing data-id="element-44" />} data-id="element-43" />
          <Route path="use-cases/founders-execs" element={<FoundersExecs data-id="element-46" />} data-id="element-45" />
          <Route path="use-cases/client-management" element={<ClientManagement data-id="element-48" />} data-id="element-47" />
          <Route path="use-cases/growth-marketing" element={<GrowthMarketing data-id="element-50" />} data-id="element-49" />
          <Route path="blog" element={<Blog data-id="element-52" />} data-id="element-51" />
          <Route path="research" element={<Research data-id="element-54" />} data-id="element-53" />
          <Route path="case-studies" element={<CaseStudies data-id="element-56" />} data-id="element-55" />
          <Route path="changelog" element={<Changelog data-id="element-58" />} data-id="element-57" />
          <Route path="security" element={<Security data-id="element-60" />} data-id="element-59" />
          <Route path="compare/chatgpt" element={<CompareChatGPT data-id="element-62" />} data-id="element-61" />
          <Route path="demo" element={<WatchGigaDemo data-id="element-64" />} data-id="element-63" />
          <Route path="demo/book" element={<BookDemo data-id="element-66" />} data-id="element-65" />
        </Route>
      </Routes>
    </CopyModeProvider>;
}